import React, { useState, useRef, useEffect, useMemo } from 'react';
import { GEMINI_MODELS } from '../services/geminiService';
import { BrainCircuitIcon, ChevronDownIcon } from '../constants';

// --- Premium Model Selector (for Modals/Settings) ---

export const ModelSelector: React.FC<{
    label: string;
    value: string;
    onChange: (val: string) => void;
    options: { id: string; label: string; description?: string; isExperimental?: boolean; isNew?: boolean }[];
    providerName: string;
}> = ({ label, value, onChange, options, providerName }) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!isOpen) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen]);

    const selectedOption = options.find(o => o.id === value) || options[0];

    return (
        <div className="space-y-2" ref={containerRef}>
            <label className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 ml-1">{label}</label>
            <div className="relative">
                <button
                    type="button"
                    onClick={() => setIsOpen(!isOpen)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm hover:border-indigo-400 dark:hover:border-indigo-500 transition-all text-left"
                >
                    <div className="flex flex-col">
                        <span className="font-semibold text-gray-800 dark:text-gray-100">{selectedOption?.label || value || 'Standard Modell'}</span>
                        <span className="text-[10px] text-gray-500 truncate max-w-[200px]">{selectedOption?.description || `${providerName} Modell`}</span>
                    </div>
                    <div className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}>
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                    </div>
                </button>

                {isOpen && (
                    <div className="absolute z-50 w-full mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl overflow-hidden animate-scale-fade-in max-h-60 overflow-y-auto scrollbar-thin">
                        <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/20">
                            <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">Wähle ein Modell</span>
                        </div>
                        {options.map((opt) => (
                            <button
                                key={opt.id}
                                type="button"
                                onClick={() => {
                                    onChange(opt.id);
                                    setIsOpen(false);
                                }}
                                className={`w-full text-left px-4 py-3 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors border-b border-gray-100 dark:border-gray-700 last:border-0 ${value === opt.id ? 'bg-indigo-50/50 dark:bg-indigo-900/10' : ''}`}
                            >
                                <div className="flex items-center justify-between mb-0.5">
                                    <span className={`font-semibold ${value === opt.id ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-800 dark:text-gray-200'}`}>
                                        {opt.label}
                                    </span>
                                    <div className="flex gap-1">
                                        {opt.isNew && <span className="text-[9px] bg-green-500 text-white px-1.5 py-0.5 rounded-full font-bold uppercase">New</span>}
                                        {opt.isExperimental && <span className="text-[9px] bg-amber-500 text-white px-1.5 py-0.5 rounded-full font-bold uppercase">Exp</span>}
                                    </div>
                                </div>
                                {opt.description && <p className="text-[11px] text-gray-500 dark:text-gray-400 line-clamp-1">{opt.description}</p>}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

// --- Compact Quick Selector (for Action Bars / Chat) ---

export const QuickModelSelector: React.FC<{
  currentModel: string;
  onSelect: (modelId: string) => void;
  providerId: string;
  customProviders: any[];
  customGeminiModels?: string[];
  position?: 'top' | 'bottom';
}> = ({ currentModel, onSelect, providerId, customProviders, customGeminiModels, position = 'top' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const models = useMemo(() => {
    if (providerId === 'gemini') {
        const customGemini = (customGeminiModels || []).map(m => ({ id: m, label: m, description: 'Benutzerdefiniertes Modell' }));
        return [...GEMINI_MODELS, ...customGemini];
    }
    const provider = customProviders.find(p => p.id === providerId);
    if (!provider) return [{ id: currentModel, label: currentModel }];
    const pModels = provider.availableModels?.length ? provider.availableModels : [provider.defaultModel ?? ''];
    return pModels.filter(Boolean).map((m: string) => ({ id: m, label: m }));
  }, [providerId, customProviders, customGeminiModels, currentModel]);

  const activeModel = models.find(m => m.id === currentModel) || { id: currentModel, label: currentModel };

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) setIsOpen(false);
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 h-8 pl-2 pr-1.5 rounded-md bg-raised border border-rule text-ink hover:border-graphite/60 transition-colors"
        title="Modell wechseln"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <BrainCircuitIcon className="h-4 w-4 text-graphite" />
        <span className="font-mono text-[11.5px] max-w-[11rem] truncate">{activeModel.label || 'Standardmodell'}</span>
        <ChevronDownIcon className={`h-3.5 w-3.5 text-graphite transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className={`absolute ${position === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'} left-0 w-64 context-menu !py-0 overflow-hidden z-50 animate-scale-fade-in max-h-64 overflow-y-auto scrollbar-thin`} role="listbox">
          <div className="px-3 py-2 border-b border-rule flex items-center justify-between">
            <span className="label-xs">Modell auswählen</span>
            <span className="font-mono text-[10px] text-graphite uppercase">{providerId}</span>
          </div>
          {models.map(m => (
            <button
              key={m.id}
              onClick={() => { onSelect(m.id); setIsOpen(false); }}
              role="option"
              aria-selected={currentModel === m.id}
              className={`w-full text-left px-3 py-2.5 hover:bg-ink/[0.06] transition-colors border-b border-rule last:border-0 ${currentModel === m.id ? 'bg-kind-prompt/[0.07]' : ''}`}
            >
              <div className="flex items-center justify-between mb-0.5">
                <span className={`font-mono text-xs font-medium ${currentModel === m.id ? 'text-kind-prompt' : 'text-ink'}`}>{m.label}</span>
                <div className="flex gap-1">
                  {m.isNew && <span className="text-[8px] bg-green-500 text-white px-1 rounded-full font-bold">NEW</span>}
                  {m.isExperimental && <span className="text-[8px] bg-amber-500 text-white px-1 rounded-full font-bold">EXP</span>}
                </div>
              </div>
              {m.description && <p className="text-[11px] text-graphite line-clamp-2 leading-tight">{m.description}</p>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
